import { authoriseOpenBankingPayment, failOpenBankingPayment, getCheckout, getMerchant } from "@api";
import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { PageLoader } from "@components";
import { useState } from "react";
import { CheckCircleFill } from "react-bootstrap-icons";
import { getCheckoutCallbackPath } from "./yapilyCallbackRouting";

/** The value as a URL when it is an absolute http(s) URL, otherwise null. */
function parseHttpUrl(value) {
  if (!value) return null;
  try {
    const url = new URL(value);
    return url.protocol === "http:" || url.protocol === "https:" ? url : null;
  } catch {
    return null;
  }
}

const YapilyCallback = () => {
  const [searchParams] = useSearchParams();
  const [success, setSuccess] = useState(false);
  const [failed, setFailed] = useState(false);

  // OB payment handline
  const paymentId = searchParams.get("p-id");
  const consent = searchParams.get("consent");
  const error = searchParams.get("error");

  const navigate = useNavigate();

  useEffect(() => {
    async function processSuccess() {
      // make return the payment
      let paymentData = await authoriseOpenBankingPayment(paymentId, consent);
      const checkoutPath = getCheckoutCallbackPath({
        source: paymentData.source,
        checkoutId: paymentData.checkoutId,
        paymentId,
      });

      if (checkoutPath) {
        navigate(checkoutPath);
        return;
      }

      switch (paymentData.source) {
        case "OntikPaymentRequest":
          navigate(`/pay/${paymentData.paymentRequestId}/processing?p-id=${paymentId}&t=openbanking`);
          break;
        case "OntikGenericPaymentLink":
          let data = await getMerchant(paymentData.merchantId);
          navigate(`/payto/${data.organisationId}/processing?p-id=${paymentId}&t=openbanking`);
          break;
        case "EmbeddedTradeCustomerCheckout":
          setSuccess(true);
          break;
        default:
          //navigate(`/pay/${paymentRequest.id}?error=bankauth`);
          break;
      }

      return;
    }

    async function processError() {
      let paymentData = await failOpenBankingPayment(paymentId, error);
      const checkoutPath = getCheckoutCallbackPath({
        source: paymentData.source,
        checkoutId: paymentData.checkoutId,
        paymentId,
        failed: true,
      });

      if (checkoutPath) {
        // A store checkout ends on the merchant's cancel URL. Going there
        // from here, rather than via the checkout page's own routing, spares
        // the shopper a flash of the payment flow while that page loads.
        // The cancel URL is whatever the merchant supplied when creating
        // the checkout, so only follow it to an HTTP(S) page.
        const checkout = await getCheckout(paymentData.checkoutId);
        const cancelUrl = parseHttpUrl(checkout?.cancelUrl);
        if (cancelUrl) {
          window.location.replace(cancelUrl.href);
          return;
        }

        navigate(checkoutPath);
        return;
      }

      switch (paymentData.source) {
        case "OntikPaymentRequest":
          navigate(`/pay/${paymentData.paymentRequestId}?error=bankauth`);
          break;
        case "OntikGenericPaymentLink":
          let data = await getMerchant(paymentData.merchantId);
          navigate(`/payto/${data.organisationId}?error=bankauth`);
          break;
        case "EmbeddedTradeCustomerCheckout":
          setFailed(true);
          break;
        default:
          //navigate(`/pay/${paymentRequest.id}?error=bankauth`);
          break;
      }

      return;
    }

    if (paymentId && error) {
      processError();
    }

    if (paymentId && consent && !error) {
      processSuccess();
    }
  }, [error, consent, paymentId]);

  if (success) {
    return <div className="w-full flex flex-col items-center">
      <h1 className="mt-8">Payment authorised</h1>
      <CheckCircleFill className="my-8 text-zinc-500" size={48} />
      <div className="text-center w-100">Your payment is now processing. You can close this tab.</div>
      <p className="text-muted fs-8 w-100 mt-auto text-center mb-2">
        Need help? Visit our <a target="_blank" rel="noreferrer noopener" href="faqs">support page</a>.
      </p>
    </div>;
  }
  if (failed) {
    return <div className="w-full flex flex-col items-center">
      <h1 className="mt-8">Payment authorisation failed</h1>
      <div className="text-center w-100">Your payment could not be authorised and no money was taken, please re-attempt payment</div>
      <p className="text-muted fs-8 w-100 mt-auto text-center mb-2">
        Need help? Visit our <a target="_blank" rel="noreferrer noopener" href="faqs">support page</a>.
      </p>
    </div>;
  }

  return (<PageLoader />);
};

export default YapilyCallback;
